import React, { useState } from 'react';
import { Check, Sparkles, TrendingUp, Crown, Building2, Zap, Gift, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { SectionIntro, SectionGlow } from '@/components/landing';

const PricingPlans = ({ onSelectPlan, currentPlan = null, isTrialExpired = false }) => {
  const { t, language } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Bilingual plan data
  const plansData = {
    id: {
      sectionTitle: "Pilih Paket Sesuai Kebutuhan",
      sectionSubtitle: "Mulai gratis, upgrade kapan saja. Tanpa biaya tersembunyi.",
      sectionEyebrow: "Paket Harga",
      sectionNote: "Semua paket sudah termasuk semua channel (WhatsApp, Instagram, Facebook) dan AI Agent.",
      trial: {
        badge: 'Gratis 30 Hari',
        period: '30 hari akses penuh',
        description: 'Coba semua fitur Nerava',
        highlights: [
          '1 AI Agent',
          '500 pesan/bulan',
          '60 pesan/hari',
          'Semua channel (WA, IG, FB)',
          'CRM & Pipeline',
          'Analytics',
          'Auto reply & Follow up'
        ],
        note: 'Akses penuh ke semua fitur selama 30 hari (max 500 pesan AI)',
        cta: 'Mulai Trial Gratis',
      },
      starter: {
        badge: 'Mulai Bisnis',
        period: 'per bulan',
        description: 'Untuk UMKM yang baru memulai',
        highlights: [
          '1 AI Agent',
          '1.700 pesan/bulan',
          '150 pesan/hari',
          'Semua channel (WA, IG, FB)',
          'CRM & Pipeline',
          'Broadcast & Follow-up',
          'Product Catalog',
          'Payment Integration',
          'Google Sheets Sync',
          'Analytics & Reports',
        ],
        cta: 'Pilih Starter',
      },
      growth: {
        badge: '⭐ RECOMMENDED',
        period: 'per bulan',
        description: 'Untuk bisnis yang berkembang',
        highlights: [
          '3 AI Agent',
          '4.600 pesan/bulan',
          '400 pesan/hari',
          'Semua channel (WA, IG, FB)',
          'CRM & Pipeline',
          'Broadcast & Follow-up',
          'Product Catalog',
          'Payment Integration',
          'Google Sheets Sync',
          'Analytics & Reports',
          'Priority Support',
        ],
        cta: 'Pilih Growth',
      },
      business: {
        badge: 'Best Value',
        period: 'per bulan',
        description: 'Untuk bisnis dengan volume tinggi',
        highlights: [
          '10 AI Agent',
          '10.000 pesan/bulan',
          '1.000 pesan/hari',
          'Semua channel (WA, IG, FB)',
          'CRM & Pipeline',
          'Broadcast & Follow-up',
          'Product Catalog',
          'Payment Integration',
          'Google Sheets Sync',
          'Analytics & Reports',
          'Priority Support',
          'API Access',
        ],
        cta: 'Pilih Business',
      },
      enterprise: {
        badge: 'Skala Besar',
        period: 'Hubungi kami',
        description: 'Solusi kustom untuk perusahaan',
        highlights: [
          'Unlimited AI Agent',
          'Unlimited pesan',
          'Semua channel (WA, IG, FB)',
          'Semua fitur Business',
          'Dedicated Support',
          'Custom Integration',
          'SLA sesuai kesepakatan',
          'Account Manager',
        ],
        cta: 'Jadwalkan Demo',
      },
    },
    en: {
      sectionTitle: "Choose Your Plan",
      sectionSubtitle: "Start free, upgrade anytime. No hidden fees.",
      sectionEyebrow: "Pricing",
      sectionNote: "Every plan already includes all channels (WhatsApp, Instagram, Facebook) and the AI Agent.",
      trial: {
        badge: 'Free 30 Days',
        period: '30 days full access',
        description: 'Try all Nerava features',
        highlights: [
          '1 AI Agent',
          '500 messages/month',
          '60 messages/day',
          'All channels (WA, IG, FB)',
          'CRM & Pipeline',
          'Analytics',
          'Auto reply & Follow up'
        ],
        note: 'Full access to all features for 30 days (max 1,000 AI messages)',
        cta: 'Start Free Trial',
      },
      starter: {
        badge: 'Start Business',
        period: 'per month',
        description: 'For small businesses just starting',
        highlights: [
          '1 AI Agent',
          '1,700 messages/month',
          '150 messages/day',
          'All channels (WA, IG, FB)',
          'CRM & Pipeline',
          'Broadcast & Follow-up',
          'Product Catalog',
          'Payment Integration',
          'Google Sheets Sync',
          'Analytics & Reports',
        ],
        cta: 'Choose Starter',
      },
      growth: {
        badge: '⭐ RECOMMENDED',
        period: 'per month',
        description: 'For growing businesses',
        highlights: [
          '3 AI Agents',
          '4,600 messages/month',
          '400 messages/day',
          'All channels (WA, IG, FB)',
          'CRM & Pipeline',
          'Broadcast & Follow-up',
          'Product Catalog',
          'Payment Integration',
          'Google Sheets Sync',
          'Analytics & Reports',
          'Priority Support',
        ],
        cta: 'Choose Growth',
      },
      business: {
        badge: 'Best Value',
        period: 'per month',
        description: 'For high-volume businesses',
        highlights: [
          '10 AI Agents',
          '12,400 messages/month',
          '1,000 messages/day',
          'All channels (WA, IG, FB)',
          'CRM & Pipeline',
          'Broadcast & Follow-up',
          'Product Catalog',
          'Payment Integration',
          'Google Sheets Sync',
          'Analytics & Reports',
          'Priority Support',
          'API Access',
        ],
        cta: 'Choose Business',
      },
      enterprise: {
        badge: 'Large Scale',
        period: 'Contact us',
        description: 'Custom solutions for enterprises',
        highlights: [
          'Unlimited AI Agents',
          'Unlimited messages',
          'All channels (WA, IG, FB)',
          'All Business features',
          'Dedicated Support',
          'Custom Integration',
          'SLA sesuai kesepakatan',
          'Account Manager',
        ],
        cta: 'Schedule a Demo',
      },
    }
  };

  const langData = plansData[language] || plansData.id;

  // Yearly prices (pay 10 months for 12 months)
  const yearlyPrices = {
    starter: { total: 1990000, monthly: 165833 },
    growth: { total: 4490000, monthly: 374166 },
    business: { total: 9990000, monthly: 832500 },
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('id-ID').format(amount);
  };

  const plans = [
    {
      id: 'trial',
      name: 'Trial',
      badge: langData.trial.badge,
      price: 0,
      priceText: 'Rp 0',
      yearlyPriceText: 'Rp 0',
      period: langData.trial.period,
      description: langData.trial.description,
      icon: <Sparkles className="w-5 h-5" />,
      color: 'blue',
      highlights: langData.trial.highlights,
      note: langData.trial.note,
      cta: langData.trial.cta,
      popular: false
    },
    {
      id: 'starter',
      name: 'Starter',
      badge: langData.starter.badge,
      price: 199000,
      priceText: 'Rp 199.000',
      yearlyPriceText: `Rp ${formatPrice(yearlyPrices.starter.monthly)}`,
      yearlyTotal: `Rp ${formatPrice(yearlyPrices.starter.total)}`,
      originalYearly: 199000 * 12,
      period: langData.starter.period,
      description: langData.starter.description,
      icon: <Zap className="w-5 h-5" />,
      color: 'blue',
      highlights: langData.starter.highlights,
      note: null,
      cta: langData.starter.cta,
      popular: false
    },
    {
      id: 'growth',
      name: 'Growth',
      badge: langData.growth.badge,
      price: 449000,
      priceText: 'Rp 449.000',
      yearlyPriceText: `Rp ${formatPrice(yearlyPrices.growth.monthly)}`,
      yearlyTotal: `Rp ${formatPrice(yearlyPrices.growth.total)}`,
      originalYearly: 449000 * 12,
      period: langData.growth.period,
      description: langData.growth.description,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'purple',
      highlights: langData.growth.highlights,
      note: null,
      cta: langData.growth.cta,
      popular: true
    },
    {
      id: 'business',
      name: 'Business',
      badge: langData.business.badge,
      price: 999000,
      priceText: 'Rp 999.000',
      yearlyPriceText: `Rp ${formatPrice(yearlyPrices.business.monthly)}`,
      yearlyTotal: `Rp ${formatPrice(yearlyPrices.business.total)}`,
      originalYearly: 999000 * 12,
      period: langData.business.period,
      description: langData.business.description,
      icon: <Crown className="w-5 h-5" />,
      color: 'gold',
      highlights: langData.business.highlights,
      note: null,
      cta: langData.business.cta,
      popular: false
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      badge: langData.enterprise.badge,
      price: null,
      priceText: 'Custom',
      yearlyPriceText: 'Custom',
      period: langData.enterprise.period,
      description: langData.enterprise.description,
      icon: <Building2 className="w-5 h-5" />,
      color: 'slate',
      highlights: langData.enterprise.highlights,
      note: null,
      cta: langData.enterprise.cta,
      popular: false
    }
  ];

  // Satu bahasa visual: indigo sebagai warna dominan, paket populer dapat aksen lebih kuat.
  const getColorClasses = (color, isPopular) => ({
    border: isPopular
      ? 'border-indigo-400 dark:border-indigo-500/70'
      : 'border-slate-200 dark:border-zinc-800',
    badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
    button: isPopular
      ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_14px_28px_-14px_rgba(79,70,229,0.8)]'
      : 'bg-transparent text-slate-900 border border-slate-300 hover:border-indigo-400 hover:text-indigo-700 dark:text-zinc-100 dark:border-zinc-700 dark:hover:border-indigo-500/70 dark:hover:text-indigo-300',
    icon: 'text-indigo-600 dark:text-indigo-400',
    ring: 'ring-indigo-500/60'
  });

  return (
    <section id="pricing" className="relative py-16 sm:py-24 px-4 bg-slate-50 dark:bg-[#0A0A0A] overflow-hidden">
      <SectionGlow position="left" />
      <div className="relative max-w-7xl mx-auto">
        <SectionIntro
          testId="pricing-heading"
          eyebrow={langData.sectionEyebrow}
          title={langData.sectionTitle}
          subtitle={langData.sectionSubtitle}
          note={langData.sectionNote}
          className="mb-12"
        />

        {/* Billing Period Toggle */}
        <div className="flex flex-col items-center gap-3 mb-10" data-testid="billing-toggle-container">
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-slate-900 dark:text-zinc-50' : 'text-slate-500 dark:text-zinc-500'}`}>
              {language === 'en' ? 'Monthly' : 'Bulanan'}
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${billingPeriod === 'yearly' ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-zinc-700'}`}
              data-testid="billing-period-toggle"
              aria-label="Toggle billing period"
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${billingPeriod === 'yearly' ? 'translate-x-7' : 'translate-x-0'} dark:bg-zinc-900`} />
            </button>
            <span className={`text-sm font-medium transition-colors ${billingPeriod === 'yearly' ? 'text-slate-900 dark:text-zinc-50' : 'text-slate-500 dark:text-zinc-500'}`}>
              {language === 'en' ? 'Yearly' : 'Tahunan'}
            </span>
          </div>
          {billingPeriod === 'yearly' && (
            <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full animate-in fade-in slide-in-from-bottom-2" data-testid="yearly-discount-badge">
              {language === 'en' ? 'Save ~17%' : 'Hemat ~17%'}
            </span>
          )}
        </div>

        {/* Mobile: Vertical stack, Desktop: 5-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-4">
          {plans.map((plan) => {
            const colorClasses = getColorClasses(plan.color, plan.popular);
            const isCurrentPlan = currentPlan === plan.id;
            const showYearly = billingPeriod === 'yearly' && plan.price > 0;
            const displayPrice = showYearly ? plan.yearlyPriceText : plan.priceText;
            const displayPeriod = showYearly
              ? (language === 'en' ? 'per month, billed yearly' : 'per bulan, tagihan tahunan')
              : plan.period;
            
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`
                  group relative flex flex-col overflow-hidden
                  bg-white dark:bg-zinc-900 rounded-2xl border
                  ${plan.popular ? 'ring-1 ' + colorClasses.ring + ' shadow-[0_28px_56px_-28px_rgba(79,70,229,0.45)]' : ''}
                  ${colorClasses.border}
                  transition-[border-color,box-shadow,transform] duration-300
                  hover:-translate-y-1 hover:border-indigo-400 hover:shadow-[0_24px_48px_-24px_rgba(79,70,229,0.4)]
                  dark:hover:border-indigo-500/70
                `}
              >
                {/* Popular Badge — menempel di tepi atas kartu supaya tidak terpotong */}
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                    <span className="flex items-center bg-indigo-600 text-white px-4 py-1 rounded-b-lg text-[11px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap shadow-[0_10px_20px_-12px_rgba(79,70,229,0.9)]">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className={`p-5 sm:p-6 ${plan.popular ? 'pt-10 sm:pt-11' : ''} flex-1 flex flex-col`}>
                  {/* Header */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`grid place-items-center w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 ${colorClasses.icon}`}>{plan.icon}</span>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-zinc-50">{plan.name}</h3>
                    </div>
                    {!plan.popular && (
                      <span className={`${colorClasses.badge} px-2 py-0.5 rounded-full text-xs font-medium`}>
                        {plan.badge}
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-zinc-50" data-testid={`price-${plan.id}`}>
                        {displayPrice}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-zinc-400">{displayPeriod}</p>
                    {showYearly && plan.yearlyTotal && (
                      <div className="mt-1.5 space-y-0.5">
                        <p className="text-xs text-slate-500 dark:text-zinc-500 line-through">
                          Rp {formatPrice(plan.originalYearly)}/{language === 'en' ? 'yr' : 'thn'}
                        </p>
                        <p className="text-xs font-semibold text-green-600 dark:text-green-400" data-testid={`yearly-total-${plan.id}`}>
                          {plan.yearlyTotal}/{language === 'en' ? 'year' : 'tahun'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-slate-600 dark:text-zinc-300 mb-4">
                    {plan.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.highlights.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-700 dark:text-zinc-300">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colorClasses.icon}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Note */}
                  {plan.note && (
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mb-4 italic">
                      {plan.note}
                    </p>
                  )}

                  {/* CTA Button */}
                  <Button
                    onClick={() => onSelectPlan?.(plan.id, billingPeriod)}
                    className={`w-full rounded-full ${colorClasses.button}`}
                    disabled={isCurrentPlan}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {isCurrentPlan ? (language === 'en' ? 'Current Plan' : 'Paket Saat Ini') : plan.cta}
                  </Button>
                </div>

                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-indigo-500 to-blue-500 transition-all duration-300 group-hover:w-full"
                />
              </div>
            );
          })}
        </div>

        {/* Pay As You Go note */}
        <div className="mt-12 max-w-3xl mx-auto" data-testid="payg-note">
          <p className="border-l-2 border-indigo-500/40 pl-4 text-sm leading-relaxed text-slate-600 dark:text-zinc-300">
            {language === 'en'
              ? 'Quota running out? Buy extra message credits (Pay As You Go) from Rp 49.000 for 500 messages — they never expire and are used automatically once your plan quota runs out. No forced upgrade.'
              : 'Kuota hampir habis? Beli kredit pesan tambahan (Pay As You Go) mulai Rp 49.000 untuk 500 pesan — tidak hangus dan otomatis dipakai begitu kuota paket habis. Tidak perlu buru-buru upgrade.'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingPlans;
